/**
 * Enhanced session security utilities
 */
import { securityLogger } from './securityLogger';
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from './production-logger';

interface SessionMetrics {
  loginTime: number;
  lastActivity: number;
  pageViews: number;
  ipAddress?: string;
}

class SessionSecurity {
  private sessionTimeout = 30 * 60 * 1000; // 30 minutes
  private warningTime = 5 * 60 * 1000; // 5 minutes before timeout
  private metrics: SessionMetrics | null = null;
  private timeoutWarningShown = false;
  private initialized = false;

  /**
   * Initialize session tracking (deferred until first user interaction)
   */
  private init() {
    if (this.initialized) return;
    this.initialized = true;
    
    this.initSessionTracking();
    this.setupActivityListeners();
  }

  /**
   * Initialize session tracking
   */
  private initSessionTracking() {
    // CRITICAL: Check sessionStorage exists before accessing (prevents SSR crash)
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
    
    const stored = sessionStorage.getItem('session_metrics');
    if (stored) {
      try {
        this.metrics = JSON.parse(stored);
      } catch (error) {
        securityLogger.logEvent({
          event_type: 'suspicious_activity',
          details: { description: 'Invalid session metrics detected' },
          severity: 'medium'
        });
      }
    }
  }

  /**
   * Start tracking a new session
   */
  startSession(userId?: string) {
    this.init(); // Ensure initialized before starting session
    
    this.metrics = {
      loginTime: Date.now(),
      lastActivity: Date.now(),
      pageViews: 1
    };
    
    this.saveMetrics();
    this.scheduleTimeoutCheck();

    securityLogger.logEvent({
      event_type: 'admin_action',
      user_id: userId,
      details: { action: 'session_started' },
      severity: 'low'
    });
  }

  /**
   * Update session activity
   */
  updateActivity() {
    this.init(); // Ensure initialized on first activity
    
    if (this.metrics) {
      this.metrics.lastActivity = Date.now();
      this.metrics.pageViews++;
      this.saveMetrics();
      this.timeoutWarningShown = false;
    }
  }

  /**
   * Check if session is expired
   */
  isSessionExpired(): boolean {
    if (!this.metrics) return true;
    
    const now = Date.now();
    const timeSinceActivity = now - this.metrics.lastActivity;
    
    return timeSinceActivity > this.sessionTimeout;
  }

  /**
   * Get time until session timeout
   */
  getTimeUntilTimeout(): number {
    if (!this.metrics) return 0;
    
    const timeElapsed = Date.now() - this.metrics.lastActivity;
    return Math.max(0, this.sessionTimeout - timeElapsed);
  }

  /**
   * Should show timeout warning
   */
  shouldShowTimeoutWarning(): boolean {
    if (this.timeoutWarningShown) return false;
    
    const timeUntilTimeout = this.getTimeUntilTimeout();
    return timeUntilTimeout <= this.warningTime && timeUntilTimeout > 0;
  }

  /**
   * Mark timeout warning as shown
   */
  markWarningShown() {
    this.timeoutWarningShown = true;
  }

  /**
   * End session and cleanup
   */
  endSession() {
    if (this.metrics) {
      const sessionDuration = Date.now() - this.metrics.loginTime;
      
      securityLogger.logEvent({
        event_type: 'admin_action',
        details: { 
          action: 'session_ended',
          duration_minutes: Math.round(sessionDuration / 60000),
          page_views: this.metrics.pageViews
        },
        severity: 'low'
      });
    }
    
    this.metrics = null;
    sessionStorage.removeItem('session_metrics');
  }

  /**
   * Force logout due to security concerns
   */
  async forceLogout(reason: string) {
    securityLogger.logEvent({
      event_type: 'suspicious_activity',
      details: { 
        description: 'Forced logout',
        reason 
      },
      severity: 'high'
    });

    await supabase.auth.signOut();
    this.endSession();
  }

  /**
   * Save metrics to session storage
   */
  private saveMetrics() {
    if (this.metrics) {
      sessionStorage.setItem('session_metrics', JSON.stringify(this.metrics));
    }
  }

  /**
   * Setup activity listeners (deferred initialization)
   */
  private setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    // Debounce activity updates to reduce overhead
    let activityTimeout: NodeJS.Timeout;
    const debouncedUpdate = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => this.updateActivity(), 500);
    };
    
    events.forEach(event => {
      document.addEventListener(event, debouncedUpdate, { passive: true });
    });
  }

  /**
   * Schedule timeout check (reduced frequency)
   */
  private scheduleTimeoutCheck() {
    setInterval(() => {
      if (this.isSessionExpired()) {
        this.forceLogout('Session timeout');
      } else if (this.shouldShowTimeoutWarning()) {
        this.showTimeoutWarning();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes (reduced from 1 minute)
  }

  /**
   * Show timeout warning
   */
  private showTimeoutWarning() {
    this.markWarningShown();
    
    // Could integrate with toast system here
    productionLogger.warn('Session will expire soon');
  }
}

export const sessionSecurity = new SessionSecurity();